// src/services/customerGroupService.js
import ApiService from './api'
import AppURLs from '../config/urls'
import { CustomerGroupModel } from '../utils/customerGroupModel'

class CustomerGroupService {
  
  // 👥 Get all customer groups with pagination support
  async getAllCustomerGroups(params = {}) {
    try {
      console.log('👥 Fetching all customer groups with params:', params)
      
      const response = await ApiService.get(AppURLs.CUSTOMER_GROUPS.GET_ALL, params)
      console.log('✅ Customer groups fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching customer groups:', error)
      throw error
    }
  }

  // 👥 Get customer group by ID
  async getCustomerGroupById(id) {
    try {
      console.log('👥 Fetching customer group by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMER_GROUPS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Customer group fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching customer group:', error)
      throw error
    }
  }

  // 👥 Create new customer group
  async createCustomerGroup(groupData) {
    try {
      console.log('👥 Creating new customer group with data:', groupData)
      
      // Validate required fields
      this.validateCustomerGroupData(groupData)
      
      const response = await ApiService.post(AppURLs.CUSTOMER_GROUPS.CREATE, groupData)
      console.log('✅ Customer group created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating customer group:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        // Backend validation error
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        // Conflict error (duplicate name)
        const conflictError = new Error(error.response.data?.message || 'Customer group with this name already exists')
        conflictError.response = error.response
        throw conflictError
      }
      
      throw error
    }
  }

  // 👥 Update customer group using PATCH
  async updateCustomerGroup(id, groupData) {
    try {
      console.log('👥 Updating customer group:', { id, groupData })
      
      // Validate partial data for updates
      this.validatePartialCustomerGroupData(groupData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMER_GROUPS.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, groupData)
      console.log('✅ Customer group updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating customer group:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        // Backend validation error
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        // Conflict error (duplicate name)
        const conflictError = new Error(error.response.data?.message || 'Group data conflicts with existing record')
        conflictError.response = error.response
        throw conflictError
      } else if (error.response?.status === 404) {
        // Not found error
        const notFoundError = new Error('Customer group not found')
        notFoundError.response = error.response
        throw notFoundError
      }
      
      throw error
    }
  }

  // 👥 Delete customer group
  async deleteCustomerGroup(id) {
    try {
      console.log('👥 Deleting customer group:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMER_GROUPS.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Customer group deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting customer group:', error)
      
      if (error.response?.status === 404) {
        const notFoundError = new Error('Customer group not found')
        notFoundError.response = error.response
        throw notFoundError
      } else if (error.response?.status === 409) {
        const conflictError = new Error('Cannot delete customer group. Group may have associated data.')
        conflictError.response = error.response
        throw conflictError
      }
      
      throw error
    }
  }

  // 👥 Search customer groups
  async searchCustomerGroups(searchParams) {
    try {
      console.log('👥 Searching customer groups with params:', searchParams)
      const response = await ApiService.get(AppURLs.CUSTOMER_GROUPS.SEARCH, searchParams)
      console.log('✅ Customer group search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching customer groups:', error)
      throw error
    }
  }

  // 👥 Validate group name availability
  async validateName(name, excludeId = null) {
    try {
      console.log('👥 Validating group name:', name)
      const params = { name }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.CUSTOMER_GROUPS.VALIDATE_NAME, params)
      console.log('✅ Name validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating group name:', error)
      throw error
    }
  }

  // 👥 Get customer group statistics
  async getCustomerGroupStats(params = {}) {
    try {
      console.log('👥 Fetching customer group statistics')
      const response = await ApiService.get(AppURLs.CUSTOMER_GROUPS.STATS, params)
      console.log('✅ Customer group stats fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching customer group stats:', error)
      throw error
    }
  }

  // 👥 Update customer group status (active/inactive)
  async updateCustomerGroupStatus(id, isActive) {
    try {
      console.log('👥 Updating customer group status:', { id, isActive })
      const response = await this.updateCustomerGroup(id, { isActive })
      console.log('✅ Customer group status updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating customer group status:', error)
      throw error
    }
  }

  // 👥 Add customers to group
  async addCustomersToGroup(groupId, customerIds) {
    try {
      console.log('👥 Adding customers to group:', { groupId, customerIds })
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMER_GROUPS.ADD_CUSTOMERS, { id: groupId })
      const response = await ApiService.post(endpoint, { customers: customerIds })
      console.log('✅ Customers added to group successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error adding customers to group:', error)
      throw error
    }
  }

  // 👥 Remove customers from group
  async removeCustomersFromGroup(groupId, customerIds) {
    try {
      console.log('👥 Removing customers from group:', { groupId, customerIds })
      const endpoint = ApiService.replaceUrlParams(AppURLs.CUSTOMER_GROUPS.REMOVE_CUSTOMERS, { id: groupId })
      const response = await ApiService.post(endpoint, { customers: customerIds })
      console.log('✅ Customers removed from group successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error removing customers from group:', error)
      throw error
    }
  }

  // 👥 Bulk delete customer groups
  async bulkDeleteCustomerGroups(groupIds) {
    try {
      console.log('👥 Bulk deleting customer groups:', groupIds)
      const response = await ApiService.post(AppURLs.CUSTOMER_GROUPS.BULK_DELETE, { groupIds })
      console.log('✅ Bulk delete completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk delete:', error)
      throw error
    }
  }

  // 👥 Bulk update customer group status
  async bulkUpdateStatus(groupIds, isActive) {
    try {
      console.log('👥 Bulk updating customer group status:', { groupIds, isActive })
      const response = await ApiService.post(AppURLs.CUSTOMER_GROUPS.BULK_STATUS, { groupIds, isActive })
      console.log('✅ Bulk status update completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk status update:', error)
      throw error
    }
  }

  // 👥 Client-side validation using CustomerGroupModel
  validateCustomerGroupData(groupData) {
    const validation = CustomerGroupModel.validate(groupData)
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0]
      throw new Error(firstError)
    }
  }

  // 👥 Client-side partial validation for updates
  validatePartialCustomerGroupData(groupData) {
    const validation = CustomerGroupModel.validatePartial(groupData)
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0]
      throw new Error(firstError)
    }
  }

  // 👥 Format customer group data for API
  formatCustomerGroupForAPI(groupData) {
    const formatted = {
      name: groupData.name?.trim(),
      description: groupData.description?.trim() || '',
      isActive: groupData.isActive !== false
    }

    // Include customers array only if it exists and has items
    if (groupData.customers && Array.isArray(groupData.customers)) {
      // Extract customer IDs if customers are objects
      formatted.customers = CustomerGroupModel.extractCustomerIds(groupData.customers)
    }

    return formatted
  }

  // 👥 Format customer group data for display
  formatCustomerGroupForDisplay(group) {
    if (!group) return null
    
    return {
      ...group,
      formattedName: CustomerGroupModel.formatName(group),
      customerCount: CustomerGroupModel.getCustomerCount(group),
      formattedCustomerNames: CustomerGroupModel.formatCustomerNames(group),
      formattedCreatedAt: CustomerGroupModel.formatDate(group.createdAt),
      formattedUpdatedAt: CustomerGroupModel.formatDate(group.updatedAt),
      statusBadgeClasses: CustomerGroupModel.getStatusBadgeClasses(group.isActive)
    }
  }

  // 👥 Filter customer groups by various criteria
  filterCustomerGroups(groups, filters) {
    if (!Array.isArray(groups)) return []
    
    return groups.filter(group => {
      // Search by name or description
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase()
        const nameMatch = group.name?.toLowerCase().includes(searchTerm)
        const descMatch = group.description?.toLowerCase().includes(searchTerm)
        if (!nameMatch && !descMatch) return false
      }
      
      // Filter by status
      if (filters.status !== undefined && filters.status !== '') {
        const isActive = filters.status === 'active'
        if (group.isActive !== isActive) return false
      }
      
      // Filter by customer count range
      if (filters.minCustomers !== undefined) {
        const customerCount = CustomerGroupModel.getCustomerCount(group)
        if (customerCount < filters.minCustomers) return false
      }
      
      if (filters.maxCustomers !== undefined) {
        const customerCount = CustomerGroupModel.getCustomerCount(group)
        if (customerCount > filters.maxCustomers) return false
      }
      
      return true
    })
  }

  // 👥 Get available customers (not in any group or specific group)
  async getAvailableCustomers(excludeGroupId = null) {
    try {
      console.log('👥 Fetching available customers for grouping')
      
      // This would typically be a specific API endpoint
      // For now, we'll use the regular customers endpoint
      // and filter on the frontend
      const CustomerService = await import('./customerService')
      const response = await CustomerService.default.getAllCustomers({ 
        page: 1, 
        limit: 1000,
        isActive: true 
      })
      
      if (response?.success) {
        const customers = response.data?.customers || []
        console.log('✅ Available customers fetched:', customers.length)
        return {
          success: true,
          data: { customers }
        }
      }
      
      return response
    } catch (error) {
      console.error('❌ Error fetching available customers:', error)
      throw error
    }
  }

  // 👥 Sort customer groups
  sortCustomerGroups(groups, sortBy = 'name', sortOrder = 'asc') {
    return CustomerGroupModel.sortGroups(groups, sortBy, sortOrder)
  }

  // 👥 Calculate group statistics
  calculateGroupStats(groups) {
    return CustomerGroupModel.calculateGroupStats(groups)
  }
}

export default new CustomerGroupService()